import React, { useState } from "react";
import { Download, FileText, Sparkles, Check } from "lucide-react";
import { resources as mockResources } from "../mock/mock";
import { useToast } from "../hooks/use-toast";
import { resourceUnlock, getResources, downloadUrl } from "../lib/api";

export default function Resources() {
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [resources, setResources] = useState(mockResources);

  useEffect(() => {
    getResources().then((data) => { if (Array.isArray(data) && data.length) setResources(data); }).catch(() => {});
  }, []);

  const unlock = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await resourceUnlock(email);
      setUnlocked(true);
      toast({ title: "Library unlocked!", description: "All downloads are now available. Check your inbox too." });
      setEmail("");
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again in a moment.", variant: "destructive" });
    }
  };

  const grab = (res) => {
    if (!unlocked) {
      toast({ title: "Enter your email first", description: "Unlock the whole library with one email — it's free.", variant: "destructive" });
      return;
    }
    window.open(downloadUrl(res.id), "_blank");
    toast({ title: "Downloading…", description: `${res.title} (PDF) is on its way.` });
  };

  return (
    <div>
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-5 pt-16 pb-10 text-center">
        <p className="font-ui tracking-[0.3em] text-teal text-sm font-semibold uppercase flex items-center justify-center gap-2">
          <FileText size={16} /> Resource Library
        </p>
        <h1 className="font-serif-display text-5xl md:text-6xl font-bold text-navy mt-4">Free tools that actually help.</h1>
        <p className="font-ui text-navy/70 mt-5 text-lg leading-relaxed max-w-2xl mx-auto">
          Templates, calculators, checklists, and planners we built for our own businesses — now yours to download.
          One email unlocks the whole library.
        </p>
      </section>

      {/* Unlock bar */}
      <section className="max-w-2xl mx-auto px-5">
        <div className={`rounded-2xl p-6 transition-colors ${unlocked ? "bg-teal/15 border border-teal" : "bg-navy text-cream"}`}>
          {unlocked ? (
            <div className="flex items-center gap-3 justify-center text-navy font-ui font-semibold">
              <Check size={20} className="text-teal" /> Library unlocked — download anything below.
            </div>
          ) : (
            <form onSubmit={unlock} className="flex flex-col sm:flex-row gap-3 items-center">
              <div className="flex items-center gap-2 text-cream font-ui font-medium shrink-0">
                <Sparkles size={18} className="text-teal" /> Unlock all downloads:
              </div>
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                className="flex-1 w-full px-4 py-3 rounded-full bg-white/10 text-cream placeholder-white/50 outline-none focus:ring-2 ring-teal font-ui text-sm"
              />
              <button type="submit" className="shrink-0 bg-teal text-navy px-6 py-3 rounded-full font-ui font-semibold hover:bg-cream transition-colors">
                Unlock Free
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-6xl mx-auto px-5 py-14">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {resources.map((r) => (
            <div key={r.title} className="lift bg-white rounded-2xl overflow-hidden border flex flex-col" style={{ borderColor: "var(--cream-dark)" }}>
              <div className="relative h-44 overflow-hidden">
                <img src={r.img} alt={r.title} className="w-full h-full object-cover" />
                <span className="absolute top-3 left-3 font-ui text-xs font-semibold px-3 py-1 rounded-full bg-cream/95 text-navy">{r.type}</span>
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-serif-display text-xl font-semibold text-navy leading-tight">{r.title}</h3>
                <p className="font-ui text-sm text-navy/70 mt-2 leading-relaxed flex-1">{r.desc}</p>
                <button onClick={() => grab(r)} className={`mt-5 inline-flex items-center justify-center gap-2 py-3 rounded-full font-ui font-semibold transition-colors ${unlocked ? "bg-navy text-cream hover:bg-orange" : "bg-cream-dark text-navy/60"}`}>
                  <Download size={17} /> {unlocked ? "Download" : "Locked"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
